import { Currency } from "@redotech/money/currencies";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { CreateBundleMessageType } from "@redotech/redo-model/conversation/message-parsers/create-bundle-message-parser";
import {
  DiscountPercentage,
  percentageToDecimal,
} from "@redotech/redo-model/discount";
import { GidType, toGid } from "@redotech/redo-model/shopify/gid-utils";
import { getProduct } from "@redotech/shopify-storefront";
import { ShopifyStorefrontClient } from "@redotech/shopify-storefront/storefront-client";
import { sinkPromise } from "@redotech/util/promise";
import { memo, useCallback, useContext, useMemo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "../../arbiter-components/checkbox/redo-checkbox";
import { RedoListItem } from "../../arbiter-components/list/redo-list";
import { RedoSpinner } from "../../arbiter-components/loading/redo-spinner";
import { RedoSingleSelectDropdownInput } from "../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { CurrencyContext } from "../../currency";
import { Flex } from "../../flex";
import { compareVariantsByTitle } from "../../product/variant-sort";
import { Text } from "../../text";
import { HandleCreateBundleFn } from "../common";
import { Carousel } from "../product-display-carousel";
import * as productDisplayCarouselCss from "../product-display-carousel.module.css";
import { ChatContentRenderer } from "./chat-content-renderer";
import { GenericRejectedState } from "./chat-content-states";
import { getToolOutput, hasToolError } from "./tool-message-utils";

interface CreateBundleToolMessageProps {
  message: CreateBundleMessageType;
  onCreateBundle?: HandleCreateBundleFn;
  readOnly?: boolean;
  conversationId: string;
  storeUrl?: string;
  storefrontAccessToken?: string;
}

interface BundleProduct {
  id: string;
  title: string;
  image?: { src: string; alt: string };
  priceRange: {
    minPrice: { amount: string; currency: string };
    maxPrice: { amount: string; currency: string };
  };
  variants: { id: string; title: string }[];
}

interface ProductMapEntry {
  isSelected: boolean;
  product: BundleProduct;
  selectedVariantId: string;
}

interface BundleProductTileProps {
  productEntry: ProductMapEntry;
  quantity: number;
  discountPercentage?: DiscountPercentage;
  onSelectionChange: (selected: boolean) => void;
  onVariantChange: (variantId: string) => void;
}

const BundleProductTile = memo(function BundleProductTile({
  productEntry,
  quantity,
  discountPercentage = 0 as DiscountPercentage,
  onSelectionChange,
  onVariantChange,
}: BundleProductTileProps) {
  const { product, isSelected, selectedVariantId } = productEntry;
  const { formatCurrency } = useContext(CurrencyContext);

  const priceRangeString = useMemo(() => {
    if (product.priceRange) {
      const minPrice = formatCurrency(+product.priceRange.minPrice.amount);
      const maxPrice = formatCurrency(+product.priceRange.maxPrice.amount);
      if (minPrice === maxPrice) {
        return minPrice;
      } else {
        return `${minPrice} - ${maxPrice}`;
      }
    }
    return undefined;
  }, [product.priceRange, formatCurrency]);

  const discountedPrice = useMemo(() => {
    if (!product.priceRange || discountPercentage === 0) {
      return null;
    }
    const discountedAmount =
      +product.priceRange.minPrice.amount *
      (1 - percentageToDecimal(discountPercentage));
    return formatCurrency(discountedAmount);
  }, [product.priceRange, discountPercentage, formatCurrency]);

  const variantOptions: RedoListItem<{ id: string; title: string }>[] =
    useMemo(() => {
      return [...product.variants]
        .sort(compareVariantsByTitle)
        .map((variant) => ({
          id: variant.id,
          value: variant,
          type: "text",
          text: variant.title,
        }));
    }, [product.variants]);

  const selectedOption = useMemo(() => {
    return (
      variantOptions.find((option) => option.value.id === selectedVariantId) ||
      variantOptions[0]
    );
  }, [variantOptions, selectedVariantId]);

  const handleVariantChange = useCallback(
    (item: RedoListItem<{ id: string; title: string }>) => {
      onVariantChange(item.value.id);
    },
    [onVariantChange],
  );

  return (
    <Flex
      className={productDisplayCarouselCss.productTileContainer}
      dir="column"
      gap="lg"
      radius="sm"
    >
      <Flex dir="column" gap="lg">
        <Flex className={productDisplayCarouselCss.productTileImageContainer}>
          <img
            alt={product.image?.alt}
            className={productDisplayCarouselCss.productTileImage}
            src={product.image?.src}
          />
        </Flex>
        <Text
          className={productDisplayCarouselCss.productTileTitle}
          color="primary"
          fontSize="sm"
          fontWeight="semibold"
          px="md"
        >
          {product.title}
        </Text>
        {discountedPrice ? (
          <Flex align="center" gap="xs" px="md">
            <Text
              fontSize="xs"
              style={{ textDecoration: "line-through" }}
              textColor="secondary"
            >
              {priceRangeString}
            </Text>
            <Text fontSize="sm" fontWeight="medium" textColor="success">
              {discountedPrice}
            </Text>
          </Flex>
        ) : (
          <Text color="secondary" fontSize="xs" fontWeight="regular" px="md">
            {priceRangeString}
          </Text>
        )}
      </Flex>
      {!!product.variants.length && (
        <Flex dir="column" gap="md" pb="md" pt="sm" px="md">
          <RedoSingleSelectDropdownInput
            options={variantOptions}
            optionSelected={({ item }) => handleVariantChange(item)}
            placeholder="Select variant"
            selectedItem={selectedOption}
          />
          <Flex align="center" gap="sm" pb="md">
            <RedoCheckbox
              label="Select product"
              setValue={onSelectionChange}
              size="xs"
              value={isSelected}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});

const CreateBundlePendingState = memo(function CreateBundlePendingState({
  message,
  onCreateBundle,
  readOnly = false,
  conversationId,
  storeUrl,
  storefrontAccessToken,
}: CreateBundleToolMessageProps) {
  const { structuredContent } = message;
  const { setCurrency } = useContext(CurrencyContext);

  const [confirmButtonPending, setConfirmButtonPending] = useState(false);
  const [cancelButtonPending, setCancelButtonPending] = useState(false);
  const [productMap, setProductMap] = useState<Map<string, ProductMapEntry>>(
    new Map(),
  );

  const productsResult = useLoad(async () => {
    if (
      !structuredContent.recommendedProducts.length ||
      !storeUrl ||
      !storefrontAccessToken
    ) {
      return new Map<string, ProductMapEntry>();
    }

    try {
      const client = new ShopifyStorefrontClient(
        storeUrl,
        storefrontAccessToken,
      );

      const productDetailsPromises = structuredContent.recommendedProducts.map(
        async (productId) => {
          try {
            const productGid = toGid(GidType.PRODUCT, productId);
            const product = await getProduct(client, productGid);

            if (!product) {
              throw new Error(`Product with ID ${productId} not found`);
            }

            const currency = product.priceRange.minVariantPrice.currencyCode;
            setCurrency(currency as unknown as Currency);

            const bundleProduct: BundleProduct = {
              id: productId,
              title: product.title,
              image: product.featuredImage
                ? {
                    src: product.featuredImage.url,
                    alt: product.featuredImage.altText || product.title,
                  }
                : undefined,
              priceRange: {
                minPrice: {
                  amount: product.priceRange.minVariantPrice.amount,
                  currency: product.priceRange.minVariantPrice.currencyCode,
                },
                maxPrice: {
                  amount: product.priceRange.maxVariantPrice.amount,
                  currency: product.priceRange.maxVariantPrice.currencyCode,
                },
              },
              variants: product.variants.nodes.map(
                (variant: { id: string; title: string }) => ({
                  id: variant.id.split("/").pop() || variant.id,
                  title:
                    variant.title === "Default Title"
                      ? product.title
                      : variant.title,
                }),
              ),
            };

            return {
              id: productId,
              entry: {
                isSelected: true,
                product: bundleProduct,
                selectedVariantId: bundleProduct.variants[0]?.id || "not-found",
              },
            };
          } catch (error) {
            console.error(`Error fetching product ${productId}:`, error);
            return null;
          }
        },
      );

      const productResults = await Promise.all(productDetailsPromises);
      const productMap = new Map<string, ProductMapEntry>();

      for (const result of productResults) {
        if (result !== null) {
          productMap.set(result.id, result.entry);
        }
      }

      return productMap;
    } catch (error) {
      console.error("Error fetching products:", error);
      throw error;
    }
  }, [
    structuredContent.recommendedProducts,
    storeUrl,
    storefrontAccessToken,
    setCurrency,
  ]);

  const handleProductSelection = useCallback(
    (productId: string, selected: boolean) => {
      setProductMap((prev) => {
        const newMap = new Map(prev);
        const entry = newMap.get(productId);
        if (entry) {
          newMap.set(productId, { ...entry, isSelected: selected });
        }
        return newMap;
      });
    },
    [],
  );

  const handleVariantChange = useCallback(
    (productId: string, variantId: string) => {
      setProductMap((prev) => {
        const newMap = new Map(prev);
        const entry = newMap.get(productId);
        if (entry) {
          newMap.set(productId, { ...entry, selectedVariantId: variantId });
        }
        return newMap;
      });
    },
    [],
  );

  const confirmCreateBundle = useHandler(async (confirmCreate: boolean) => {
    if (confirmCreate) {
      setConfirmButtonPending(true);
    } else {
      setCancelButtonPending(true);
    }

    // Get variant IDs for selected products
    const variantIds: number[] = [];
    if (confirmCreate) {
      for (const entry of Array.from(productMap.values()).filter(
        (entry) => entry.isSelected,
      )) {
        if (entry.selectedVariantId) {
          variantIds.push(parseInt(entry.selectedVariantId, 10));
        }
      }
    }

    await onCreateBundle?.({
      conversationId,
      pendingToolCallId: message.pendingToolCallId ?? "",
      confirmationData: {
        ...structuredContent,
        recommendedProducts: structuredContent.recommendedProducts.filter(
          (productId) => {
            const entry = productMap.get(productId);
            return entry?.isSelected ?? false;
          },
        ),
        confirmCreateBundle: confirmCreate,
      },
      variantIds,
    });
  });

  const isCompleted = message.customerInputContent?.confirmed !== undefined;
  const selectedCount = Array.from(productMap.values()).filter(
    (entry) => entry.isSelected,
  ).length;

  // Initialize productMap when products are loaded
  useMemo(() => {
    if (productsResult.value && productMap.size === 0) {
      setProductMap(new Map(productsResult.value));
    }
  }, [productsResult.value, productMap.size]);

  if (productsResult.pending) {
    return (
      <Flex align="flex-end" as="form" dir="row" gap="lg" py="md" w="full">
        <Flex dir="column" gap="xl" w="full">
          <Flex dir="column" gap="md">
            <Text
              fontSize="md"
              fontWeight="semibold"
              textAlign="center"
              textColor="primary"
            >
              Personalized Bundle
            </Text>
            <Flex
              className={
                productDisplayCarouselCss.productDisplayCarouselContainer
              }
              justify="flex-start"
              position="relative"
            >
              <Carousel>
                {structuredContent.recommendedProducts.map((_, index) => (
                  <ProductLoadingTile key={index} />
                ))}
              </Carousel>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  if (productsResult.error || !productsResult.value || productMap.size === 0) {
    return (
      <Flex justify="center" p="lg">
        <Text color="error">Failed to load product details</Text>
      </Flex>
    );
  }

  return (
    <Flex align="flex-end" as="form" dir="column" gap="lg" py="md" w="full">
      <Flex dir="column" gap="xl" w="full">
        <Text
          fontSize="md"
          fontWeight="semibold"
          textAlign="center"
          textColor="primary"
        >
          Personalized Bundle
        </Text>
        <Flex
          className={productDisplayCarouselCss.productDisplayCarouselContainer}
          justify="flex-start"
          position="relative"
        >
          <Carousel>
            {Array.from(productMap.values()).map((productEntry) => (
              <BundleProductTile
                discountPercentage={
                  structuredContent.discountPercentage as DiscountPercentage
                }
                key={productEntry.product.id}
                onSelectionChange={(selected) =>
                  handleProductSelection(productEntry.product.id, selected)
                }
                onVariantChange={(variantId) =>
                  handleVariantChange(productEntry.product.id, variantId)
                }
                productEntry={productEntry}
                quantity={1}
              />
            ))}
          </Carousel>
        </Flex>
      </Flex>

      {isCompleted ? (
        <Flex alignSelf="center" dir="column" gap="sm">
          <Text fontSize="md" fontWeight="semibold" textColor="primary">
            {message.customerInputContent?.confirmed
              ? "Bundle discount code created successfully"
              : "Bundle discount code creation was cancelled"}
          </Text>
        </Flex>
      ) : (
        <Flex gap="sm" justify="center" w="full">
          <RedoButton
            disabled={readOnly || cancelButtonPending || confirmButtonPending}
            hierarchy="secondary"
            onClick={() => {
              sinkPromise(confirmCreateBundle(false));
            }}
            pending={cancelButtonPending}
            text="Cancel"
          />
          <RedoButton
            disabled={
              readOnly ||
              cancelButtonPending ||
              confirmButtonPending ||
              selectedCount === 0
            }
            hierarchy="primary"
            onClick={() => {
              sinkPromise(confirmCreateBundle(true));
            }}
            pending={confirmButtonPending}
            text={
              selectedCount > 0
                ? `Add ${selectedCount} item${selectedCount === 1 ? "" : "s"} to bundle`
                : "Select items to create bundle"
            }
          />
        </Flex>
      )}
    </Flex>
  );
});

const ProductLoadingTile = memo(function ProductLoadingTile() {
  return (
    <Flex
      className={productDisplayCarouselCss.productTileContainer}
      dir="column"
      gap="none"
      radius="sm"
    >
      <Flex
        align="center"
        className={productDisplayCarouselCss.productTileImageContainer}
        justify="center"
      >
        <RedoSpinner size="md" />
      </Flex>
    </Flex>
  );
});

interface CreateBundleConfirmationStateProps {
  message: CreateBundleMessageType;
}

const CreateBundleConfirmationState = memo(
  function CreateBundleConfirmationState({
    message,
  }: CreateBundleConfirmationStateProps) {
    const toolOutput = getToolOutput(message);
    const hasError = hasToolError(message);
    const [isCopying, setIsCopying] = useState(false);

    const handleCopyDiscountCode = useHandler(async () => {
      if (!toolOutput || hasError || isCopying) {
        return;
      }

      setIsCopying(true);
      try {
        await navigator.clipboard.writeText(toolOutput);
        await new Promise((resolve) => setTimeout(resolve, 300));
      } catch (error) {
        console.error("Failed to copy discount code:", error);
      } finally {
        setIsCopying(false);
      }
    });

    if (hasError) {
      return (
        <Text fontSize="sm" fontWeight="semibold" textColor="error">
          Bundle discount code creation failed
        </Text>
      );
    }

    return (
      <Flex dir="column" gap="sm">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          Bundle discount code created successfully
        </Text>
        {toolOutput && (
          <Flex align="center" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              Use code:
            </Text>
            <RedoButton
              hierarchy="secondary"
              onClick={handleCopyDiscountCode}
              pending={isCopying}
              pressed={isCopying}
              size="xs"
              text={toolOutput}
              textWeight="semibold"
            />
          </Flex>
        )}
      </Flex>
    );
  },
);

export const CreateBundleToolMessage = memo(function CreateBundleToolMessage(
  props: CreateBundleToolMessageProps,
) {
  const { message } = props;

  return (
    <ChatContentRenderer
      contentByState={{
        pending: <CreateBundlePendingState {...props} />,
        confirmed: (
          <Flex px="xl">
            <CreateBundleConfirmationState message={message} />
          </Flex>
        ),
        rejected: (
          <Flex px="xl">
            <GenericRejectedState
              subtitle="The bundle discount code was not created"
              title="Bundle discount code creation cancelled"
            />
          </Flex>
        ),
      }}
      message={message}
    />
  );
});
